import { CuriosityValue, CuriosityNamespaceType, CuriosityTranslationOrKey } from '@procureai/curiosity-ui-mui';
import { camelCase } from 'change-case';
import { ReactNode } from 'react';
import { Union } from 'ts-toolbelt';

import { DemandUser } from '@/legacy/common/types/DemandUser';
import { NegotiationDemandDataGridRow } from '~/common/types';
import { ChipInfoDetailsGetter, LinkCellValue } from '~/components/DataGridCustom';
import { Markdown } from '~/components/Markdown/Markdown';
import { UserEntity } from '~/components/UserAutocomplete';
import { RouteId } from '~/routes/RouteId';
import { isString } from '~/util/string';

export const capabilitiesDetailsGetter = (
  t: (key: string) => string,
): ChipInfoDetailsGetter<NegotiationDemandDataGridRow> => {
  const ns = CuriosityNamespaceType.CAPABILITY;
  return (value) => ({
    label: value && <CuriosityValue ns={ns} i18nKey={String(value)} />,
    info: {
      headline: value && <CuriosityValue ns={ns} i18nKey={String(value)} />,
      subHeadline: t(`InfoDrawer.${camelCase(ns)}`),
      description: value && (
        <CuriosityValue ns={ns} i18nKey={String(value)} valueGetter={(ct) => ct.entity.description}>
          {(val) => <Markdown>{val as string}</Markdown>}
        </CuriosityValue>
      ),
    },
  });
};

export const supplierValueGetter = (
  value: Union.Nullable<Union.Nullable<string>[] | string>,
): Union.Nullable<LinkCellValue[]> => {
  const ns = CuriosityNamespaceType.SUPPLIER;
  return value
    ? (Array.isArray(value) ? value : [value]).map((item) => {
        return {
          to: RouteId.supplierDetail(String(item)),
          label: <CuriosityValue ns={ns} i18nKey={String(item)} />,
        };
      })
    : null;
};

export const organizationValueGetter = (
  value: Union.Nullable<Union.Nullable<string>[] | string>,
): Union.Nullable<ReactNode[]> => {
  const ns = CuriosityNamespaceType.ORGANIZATION;
  return value
    ? (Array.isArray(value) ? value : [value]).map((item) => {
        return <CuriosityValue key={String(item)} ns={ns} i18nKey={String(item)} />;
      })
    : null;
};

export const curiosityValueGetter = (
  ns: CuriosityNamespaceType,
  value: Union.Nullable<Union.Nullable<string>[] | string>,
): Union.Nullable<ReactNode[]> => {
  return value
    ? (Array.isArray(value) ? value : [value]).map((item) => {
        return <CuriosityValue key={String(item)} ns={ns} i18nKey={String(item)} />;
      })
    : null;
};

export const responsiblesValueGetter = ({
  value,
}: {
  value: any;
}): Union.Nullable<CuriosityTranslationOrKey<UserEntity>> => {
  const responsible: Union.Nullable<DemandUser | string> = value && value[0];
  if (responsible) {
    return isString(responsible)
      ? responsible
      : {
          id: responsible.id,
          key: responsible.id,
          value: `${responsible.firstName} ${responsible.lastName}`,
          language: '',
          entity: {
            firstName: responsible.firstName,
            lastName: responsible.lastName,
            email: responsible.email,
          },
        };
  }
};

export const commodityGroupValueGetter = (
  value: Union.Nullable<Union.Nullable<string>[] | string>,
): Union.Nullable<ReactNode[]> => {
  const ns = CuriosityNamespaceType.COMMODITY_GROUP;
  return value
    ? (Array.isArray(value) ? value : [value]).map((item) => {
        return <CuriosityValue key={String(item)} ns={ns} i18nKey={String(item)} />;
      })
    : null;
};
